import React, { memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Typography,
} from '@mui/material';
import type { MonthlyShiftSlotSummary } from '../../../../domain/monthlyShiftSlot';
import { formatYearMonthToSlash } from '../../../formatters';
import StatusChip from '../../MonthlyShiftSlotManager/components/StatusChip';

/**
 * ConfirmedShiftMonthTable のProps型
 *
 * @property {MonthlyShiftSlotSummary[]} months - 現在ページに表示する月次シフト一覧（サーバーサイドページング済み）
 * @property {number} page - 現在のページ番号（0始まり）
 * @property {number} rowsPerPage - 1ページあたりの表示件数
 * @property {number} totalCount - ページングを含めた総件数
 * @property {function} onPageChange - ページ変更時のコールバック（変更後のページ番号を渡す）
 * @property {function} onOpenSetup - 解放枠設定画面へ遷移するコールバック（年・月を渡す）
 */
export interface ConfirmedShiftMonthTableProps {
  months: MonthlyShiftSlotSummary[];
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onOpenSetup: (year: number, month: number) => void;
}

/**
 * 確定済みシフト（月次シフト一覧）テーブル
 *
 * @description
 * 月次シフトを年月・ステータス・解放枠設定の3列で一覧表示する。
 * ステータスが「確定（confirmed）」の行のみ解放枠を設定でき、
 * 「解放枠を設定する →」ボタンに加えて行全体のクリックでも設定画面へ遷移する。
 * 確定以外の行は「確定後に設定可能」を表示し、クリックによる遷移は行わない。
 * ページングはサーバーサイドで行うため、本コンポーネントは受け取った`months`をそのまま描画する。
 *
 * @param {ConfirmedShiftMonthTableProps} props - コンポーネントProps
 * @returns {React.ReactElement} 確定済みシフト一覧テーブル
 */
const ConfirmedShiftMonthTable: React.FC<ConfirmedShiftMonthTableProps> = memo(
  ({ months, page, rowsPerPage, totalCount, onPageChange, onOpenSetup }) => {
    if (months.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          月次シフトがまだ登録されていません。
        </Typography>
      );
    }

    return (
      <Paper className="admin-reservation-slot-table" variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>年月</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>ステータス</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 180 }}>
                  解放枠
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {months.map((month) => {
                const isConfirmed = month.status === 'confirmed';

                return (
                  <TableRow
                    key={month.id}
                    hover
                    onClick={isConfirmed ? () => onOpenSetup(month.year, month.month) : undefined}
                    sx={isConfirmed ? { cursor: 'pointer' } : undefined}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {formatYearMonthToSlash(month.year, month.month)}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={month.status} />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {isConfirmed ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={(event) => {
                            // 行クリックとの二重発火を防ぐ
                            event.stopPropagation();
                            onOpenSetup(month.year, month.month);
                          }}
                        >
                          解放枠を設定する →
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          確定後に設定可能
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ページネーション（モックの「1-6 / 6件 ‹ ›」に対応） */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_e, newPage) => onPageChange(newPage)}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}件`}
        />
      </Paper>
    );
  }
);

ConfirmedShiftMonthTable.displayName = 'ConfirmedShiftMonthTable';

export default ConfirmedShiftMonthTable;
